import { useEffect, useRef, useState } from "react";
import { Upload, X, Loader2, Camera, ChevronDown, ChevronRight } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { PHOTO_BUCKET, useResolvedPhotoUrl } from "@/components/ui-extras";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PhotoCropper } from "@/components/photo-cropper";
import {
  beginNativeHandoff,
  endNativeHandoff,
} from "@/lib/native-handoff";

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;
const SIGNED_URL_DELAY_MS = 500;
const COMPRESS_THRESHOLD = 2 * 1024 * 1024;

const HINTS: Record<string, string> = {
  jewelry:
    "Place your piece on a white or neutral surface in natural light. Avoid flash — it flattens metal and washes out gemstones. Shoot from directly above for flat pieces, or at a slight angle to show dimension.",
  clothing:
    "Lay flat or hang against a clean white or neutral background. Natural window light works beautifully. Capture the full garment including neckline and hem, and smooth out any wrinkles before shooting.",
  accessories:
    "Place against a contrasting background — white for dark pieces, dark for light pieces. For bags, lightly stuff them so the shape reads clearly. Shoot shoes at a 45-degree angle to capture the silhouette.",
};

function getHintType(folder: string): string | null {
  if (folder === "jewelry" || folder === "clothing" || folder === "accessories") return folder;
  return null;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function isHeic(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    file.type === "image/heic" ||
    file.type === "image/heif" ||
    name.endsWith(".heic") ||
    name.endsWith(".heif")
  );
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not decode image"));
    img.src = src;
  });
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error("Could not read file"));
    r.readAsDataURL(file);
  });
}

function canvasToJpegFile(
  canvas: HTMLCanvasElement,
  filename: string,
  quality = 0.85,
): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Image conversion failed"));
        resolve(new File([blob], filename, { type: "image/jpeg" }));
      },
      "image/jpeg",
      quality,
    );
  });
}

async function convertHeicToJpeg(file: File): Promise<File> {
  const dataUrl = await fileToDataUrl(file);
  const img = await loadImage(dataUrl);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");
  ctx.drawImage(img, 0, 0);
  const base = file.name.replace(/\.(heic|heif)$/i, "") || "photo";
  return canvasToJpegFile(canvas, `${base}.jpg`, 0.9);
}

async function compressImage(file: File, maxDim = 2048): Promise<File> {
  try {
    const dataUrl = await fileToDataUrl(file);
    const img = await loadImage(dataUrl);
    let { naturalWidth: w, naturalHeight: h } = img;
    const scale = Math.min(1, maxDim / Math.max(w, h));
    w = Math.round(w * scale);
    h = Math.round(h * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, w, h);
    const base = file.name.replace(/\.[^.]+$/, "") || "photo";
    const compressed = await canvasToJpegFile(canvas, `${base}.jpg`, 0.82);
    return compressed.size < file.size ? compressed : file;
  } catch {
    return file;
  }
}

export function PhotoUpload({
  userId,
  folder,
  value,
  onChange,
  hidePreview = false,
  onFileCaptured,
  cameraOnly = false,
}: {
  userId: string;
  folder: "jewelry" | "clothing" | "looks" | "profile" | "accessories" | "home";
  value: string | null;
  onChange: (path: string | null, file?: File | null) => void;
  hidePreview?: boolean;
  onFileCaptured?: (file: File) => void;
  cameraOnly?: boolean;
}) {
  const libraryRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [pendingCrop, setPendingCrop] = useState<File | null>(null);
  const { url: preview } = useResolvedPhotoUrl(value);

  // Track whether this PhotoUpload currently has a native picker open so we
  // can release the handoff lock on focus return (covers the cancel case,
  // where no onChange fires on the hidden <input>).
  const handoffOpenRef = useRef(false);

  function openPicker(ref: React.RefObject<HTMLInputElement | null>) {
    handoffOpenRef.current = true;
    beginNativeHandoff();
    ref.current?.click();
  }

  function releaseHandoff() {
    if (!handoffOpenRef.current) return;
    handoffOpenRef.current = false;
    endNativeHandoff();
  }

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onFocus = () => {
      // The native picker just returned (either with a file or cancelled).
      // Defer slightly so the input's onChange has a chance to fire first.
      if (!handoffOpenRef.current) return;
      window.setTimeout(releaseHandoff, 400);
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      // If this component unmounts while a handoff is pending, release it.
      if (handoffOpenRef.current) {
        handoffOpenRef.current = false;
        endNativeHandoff();
      }
    };
  }, []);

  const hintType = getHintType(folder);
  const storageKey = hintType ? `caileve_hints_dismissed_${hintType}` : null;

  // One-time migration: clear legacy privee_ hint keys
  if (typeof window !== "undefined" && hintType) {
    try {
      const legacyKey = `privee_hints_dismissed_${hintType}`;
      const legacyValue = localStorage.getItem(legacyKey);
      if (legacyValue !== null) {
        localStorage.setItem(`caileve_hints_dismissed_${hintType}`, legacyValue);
        localStorage.removeItem(legacyKey);
      }
    } catch {
      // ignore storage errors
    }
  }
  const [hintExpanded, setHintExpanded] = useState(() => {
    if (typeof window === "undefined" || !storageKey) return true;
    return localStorage.getItem(storageKey) !== "true";
  });

  const showHint = !!hintType && !uploading && !(preview && !hidePreview);

  function toggleHint() {
    setHintExpanded((prev) => {
      const next = !prev;
      if (!next && storageKey) {
        localStorage.setItem(storageKey, "true");
      }
      return next;
    });
  }

  async function handleSelected(file: File) {
    if (!file.type.startsWith("image/") && !isHeic(file)) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Image must be under 25 MB");
      return;
    }
    // HEIC inputs aren't decodable by browsers — convert before showing the cropper.
    let prepared = file;
    if (isHeic(prepared)) {
      try {
        prepared = await convertHeicToJpeg(prepared);
      } catch {
        toast.error("Could not read HEIC photo");
        return;
      }
    }
    setFailed(false);
    setPendingCrop(prepared);
  }

  async function handleFile(file: File) {
    onFileCaptured?.(file);
    setUploading(true);
    setFailed(false);
    try {
      let prepared = file;
      if (prepared.size > COMPRESS_THRESHOLD) {
        prepared = await compressImage(prepared);
      }
      const ext = (prepared.name.split(".").pop() || "jpg").toLowerCase();
      const path = `${userId}/${folder}/${crypto.randomUUID()}.${ext}`;

      let lastErr: unknown = null;
      let uploaded = false;
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        const { error } = await supabase.storage
          .from(PHOTO_BUCKET)
          .upload(path, prepared, {
            cacheControl: "3600",
            upsert: false,
            contentType: prepared.type || "image/jpeg",
          });
        if (!error) {
          uploaded = true;
          break;
        }
        lastErr = error;
        if (attempt < MAX_RETRIES) await sleep(RETRY_DELAY_MS);
      }
      if (!uploaded) {
        throw lastErr instanceof Error ? lastErr : new Error("Upload failed");
      }

      // Let Storage finish committing before downstream signed-URL fetches.
      await sleep(SIGNED_URL_DELAY_MS);
      // Bucket is private — store only the object path. Signed URLs are
      // generated on demand by useResolvedPhotoUrl so RLS is enforced.
      onChange(path, prepared);
    } catch (e) {
      setFailed(true);
      toast.error((e as Error).message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function handleRemove() {
    if (value && !/^(https?:|data:|blob:)/i.test(value)) {
      await supabase.storage.from(PHOTO_BUCKET).remove([value]);
    }
    setFailed(false);
    onChange(null);
  }

  return (
    <div className="space-y-2">
      <input
        ref={libraryRef}
        type="file"
        accept="image/*,.heic,.heif"
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          releaseHandoff();
          if (f) handleSelected(f);
          e.target.value = "";
        }}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*,.heic,.heif"
        capture="environment"
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          releaseHandoff();
          if (f) handleSelected(f);
          e.target.value = "";
        }}
      />
      {showHint && (
        <button
          type="button"
          onClick={toggleHint}
          className="w-full text-left flex items-start gap-3 p-3 rounded-md transition-colors hover:opacity-90"
          style={{ backgroundColor: "var(--caileve-soft)" }}
        >
          <Camera className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: "var(--caileve-gold-text)" }} />
          <div className="flex-1 min-w-0">
            <p
              className="text-sm italic leading-relaxed"
              style={{
                color: "var(--caileve-muted)",
                fontFamily: "'Cormorant Garamond', ui-serif, Georgia, serif",
              }}
            >
              {hintExpanded
                ? HINTS[hintType!]
                : HINTS[hintType!].split(". ")[0] + "..."}
            </p>
            <p
              className="text-xs italic mt-1"
              style={{
                color: "var(--caileve-muted)",
                fontFamily: "'Cormorant Garamond', ui-serif, Georgia, serif",
              }}
            >
              The better the photo, the better Cailève knows your piece.
            </p>
          </div>
          {hintExpanded ? (
            <ChevronDown className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: "var(--caileve-muted)" }} />
          ) : (
            <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: "var(--caileve-muted)" }} />
          )}
        </button>
      )}
      {pendingCrop ? (
        <PhotoCropper
          file={pendingCrop}
          onConfirm={(cropped) => {
            setPendingCrop(null);
            handleFile(cropped);
          }}
          onCancel={() => setPendingCrop(null)}
        />
      ) : uploading && !hidePreview ? (
        <div
          className="relative aspect-square w-32 overflow-hidden flex items-center justify-center animate-pulse"
          style={{ backgroundColor: "var(--caileve-soft)" }}
          aria-busy="true"
          aria-label="Uploading photo"
        >
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--caileve-lilac)" }} />
        </div>
      ) : preview && !hidePreview ? (
        <div className="relative aspect-square w-32 bg-secondary overflow-hidden">
          <img src={preview} alt="Uploaded photo preview" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-1 right-1 bg-background/80 backdrop-blur p-1 hover:bg-background"
            aria-label="Remove photo"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : uploading ? (
        <div
          className="w-full flex items-center justify-center gap-2 py-3 animate-pulse"
          style={{ backgroundColor: "var(--caileve-soft)" }}
          aria-busy="true"
        >
          <Loader2 className="h-4 w-4 animate-spin" style={{ color: "var(--caileve-lilac)" }} />
          <span
            className="text-xs uppercase tracking-[0.25em]"
            style={{ color: "var(--caileve-lilac)" }}
          >
            Uploading
          </span>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            onClick={() => openPicker(cameraRef)}
            className="w-full py-3 text-xs uppercase tracking-[0.25em]"
            style={{ backgroundColor: "var(--caileve-ink)", color: "var(--caileve-bg)", border: "none" }}
          >
            <Camera className="h-4 w-4 mr-2" /> Take Photo
          </Button>
          {!cameraOnly && (
            <Button
              type="button"
              variant="outline"
              onClick={() => openPicker(libraryRef)}
              className="w-full py-3 text-xs uppercase tracking-[0.25em] border-[var(--caileve-soft)] text-[var(--caileve-ink)] hover:bg-[var(--caileve-bg)]"
            >
              <Upload className="h-4 w-4 mr-2" /> Choose from Library
            </Button>
          )}
        </div>
      )}
      {failed && !uploading && (
        <p
          className="italic text-sm"
          style={{
            color: "var(--caileve-muted)",
            fontFamily: "'Cormorant Garamond', ui-serif, Georgia, serif",
          }}
        >
          We had trouble loading your photo. Please try again.
        </p>
      )}
      {hidePreview && preview && !uploading && (
        <button
          type="button"
          onClick={handleRemove}
          className="text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-foreground transition-colors"
        >
          Remove photo
        </button>
      )}
    </div>
  );
}